import MaterialTable from 'material-table';
import React, { useEffect, useMemo } from 'react';
import { Card, CardBody, Row, Button, FormGroup, Col } from 'reactstrap';
import { NotificationManager } from '../../components/common/react-notifications';
import { useUserContext } from '../../context/UserContext';
import { Form, Formik } from 'formik';
import {
  formatReferralLink,
  getFormattedDateString,
  getFormattedMoneyString,
} from '../../helpers/utils';
import { useBasicTableState } from '../../hooks/useBasicTableState';
import userAPI from '../../services/API/user';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useLayoutContext } from '../../context/LayoutContext';
import { strings } from '../../utils/strings';
import idx from 'idx';
import { CommonFormInput } from '../../components/common/CustomFormComponents';
import * as Yup from 'yup';
import copy from 'copy-to-clipboard';
import { Colxx } from '../../components/common/CustomBootstrap';
import { PageTitleHeader } from '../../components/common/PageTitleHeader';
import AdvanceTable from '../../components/advance-table/AdvanceTable';
import AdvanceTablePagination from '../../components/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from '../../components/advance-table/AdvanceTableWrapper';
import shopAPI from '../../services/API/shop';
import { FeatureNotAvailable } from '../../components/common/feature-not-available/FeatureNotAvailable';
import { ButtonWithLoadingIcon } from '../../components/common/ButtonWithLoadingIcon';
import { BecomeMemberComponent } from '../../components/common/BecomeMemberComponent';
import { MEMBER_SUBSCRIPTION_STATUS } from '../../constants/appConst';

export const ReferAFriend = () => {
  const { shop, user } = useUserContext();
  const { showNavBar } = useLayoutContext();

  const {
    loadingItems,
    setLoadingItems,
    items: referals,
    setItems: setReferals,
    loadingDiscounts,
    setLoadingDiscounts,
    discounts: discountsList,
    setDiscounts: setDiscountsList,
  } = useBasicTableState();

  const referralLink = idx(shop, (_) => _.referral_link) || null;
  const refLink = formatReferralLink(referralLink, '12345678') || '';

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [selectedTab, setSelectedTab] = React.useState(1);
  const isAvailable = selectedTab === 1;
  const [pageSizeValue, setPageSizeValue] = React.useState(15);

  const isSM = windowWidth <= 600;

  const initialValues = {
    email: '',
  };

  useEffect(() => {
    getActiveDiscount();
    window.addEventListener('resize', () => {
      setWindowWidth(window.innerWidth);
    });
    return () => {
      window.addEventListener('resize', () => {
        setWindowWidth(window.innerWidth);
      });
    };
  }, []);

  const getActiveDiscount = async () => {
    setLoadingDiscounts(true);
    const userId = localStorage.getItem('userId');
    shopAPI
      .getActiveDiscount({ user_id: userId, available: true })
      .then((res) => {
        const discounts = idx(res, (_) => _.discounts) || [];
        setDiscountsList(discounts);
      })
      .catch(() => {
        NotificationManager.error({
          message: 'Something went wrong',
        });
        setLoadingDiscounts(false);
      })
      .finally(() => {
        setLoadingDiscounts(false);
      });
  };

  const getRedeemedDiscount = async () => {
    setLoadingDiscounts(true);
    let userId = localStorage.getItem('userId');
    shopAPI
      .getRedeemedDiscount({ user_id: userId, available: false })
      .then((res) => {
        const discounts = idx(res, (_) => _.discounts) || [];
        setDiscountsList(discounts);
      })
      .catch(() => {
        NotificationManager.error({
          message: 'Something went wrong',
        });
        setLoadingDiscounts(false);
      })
      .finally(() => {
        setLoadingDiscounts(false);
      });
  };

  const emailValidationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Please enter the email')
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, 'Invalid email'),
  });

  useEffect(() => {
    setLoadingItems(true);
    userAPI
      .getReferals(user.id)
      .then((refers) => {
        setReferals(refers);
      })
      .catch(() => {
        NotificationManager.error({
          message: 'Something went wrong',
        });
      })
      .finally(() => {
        setLoadingItems(false);
      });
  }, []);

  const cols = useMemo(
    () => [
      {
        Header: 'DATE',
        accessor: 'recorded',
        cellProps: { style: { whiteSpace: 'nowrap' } },
        Cell: (rowData) => {
          const { recorded } = rowData.row.original;
          return (
            <span style={{ fontSize: 14 }}>
              {getFormattedDateString(recorded)}
            </span>
          );
        },
      },
      {
        Header: 'NAME',
        sortType: (rowA, rowB, id, desc) => {
          const a = rowA?.original[id]?.toLowerCase() ?? '';
          const b = rowB?.original[id]?.toLowerCase() ?? '';
          return a.localeCompare(b);
        },
        accessor: 'referree_name',
        cellProps: {
          style: {
            width: 600,
            maxWidth: 600,
          },
        },
        headerProps: {
          style: {
            width: 600,
            maxWidth: 600,
          },
        },
        Cell: (rowData) => {
          const { referree_name } = rowData.row.original;
          return <span style={{ fontSize: 14 }}>{referree_name || '--'}</span>;
        },
      },
      {
        Header: 'EARNED',
        accessor: 'commission_earned',
        Cell: (rowData) => {
          const { commission_earned } = rowData.row.original;
          return (
            <span style={{ fontSize: 14 }}>
              {getFormattedMoneyString(commission_earned)}
            </span>
          );
        },
      },
    ],
    []
  );

  const copyCodeToClipboard = (text) => {
    try {
      copy(text);
      NotificationManager.success({
        message: 'Copied',
      });
    } catch (e) {
      NotificationManager.error({
        message: 'Copied fail',
      });
    }
  };

  let rewardCols = [
    {
      Header: 'DATE',
      accessor: isAvailable ? 'created' : 'redeem_date',
      cellProps: { style: { whiteSpace: 'nowrap' } },
      Cell: (rowData) => {
        if (isAvailable) {
          const { created } = rowData.row.original;
          return (
            <span style={{ fontSize: 14 }}>
              {getFormattedDateString(created)}
            </span>
          );
        } else {
          const { redeem_date } = rowData.row.original;
          return (
            <span style={{ fontSize: 14 }}>
              {getFormattedDateString(redeem_date)}
            </span>
          );
        }
      },
    },
    {
      Header: 'CODE',
      accessor: 'code',
      Cell: (rowData) => {
        const { code } = rowData.row.original;
        return <span style={{ fontSize: 14 }}>{code || '--'}</span>;
      },
      cellProps: {
        style: {
          width: 400,
          maxWidth: 400,
        },
      },
      headerProps: {
        style: {
          width: 400,
          maxWidth: 400,
        },
      },
    },
    {
      Header: 'DISCOUNT',
      accessor: 'discount',
      Cell: (rowData) => {
        const { discount } = rowData.row.original;
        return <span style={{ fontSize: 14 }}>{discount}</span>;
      },
    },
  ];

  if (isAvailable) {
    rewardCols = [
      ...rewardCols,
      {
        Header: 'EXPIRATION DATE',

        accessor: 'expire_date',
        Cell: (rowData) => {
          const { ends } = rowData.row.original;
          return (
            <span style={{ fontSize: 14 }}>
              {ends ? getFormattedDateString(ends) : '-'}
            </span>
          );
        },
      },
    ];
  }

  const handleSubmit = (values, { resetForm }) => {
    const discountCode = referralLink.split('=').pop();
    const data = {
      email: values.email,
      link: refLink,
      discount_code: discountCode,
    };
    userAPI
      .sendReferral(data)
      .then((refers) => {
        NotificationManager.success({
          message: refers,
        });
        resetForm({});
      })
      .catch(() => {
        NotificationManager.error({
          message: 'Something went wrong',
        });
      })
      .finally(() => {
        setLoadingItems(false);
      });
  };

  if (loadingDiscounts || loadingItems) {
    return (
      <div
        className='loading-overlay'
        style={{ backgroundColor: 'transparent' }}
      >
        <div className='loading'></div>
      </div>
    );
  } else {
    if (user.member_status !== MEMBER_SUBSCRIPTION_STATUS.CANCELLED) {
      return (
        <Row style={!showNavBar ? { justifyContent: 'center' } : {}}>
          <Colxx xxs='12' className='mb-2'>
            <Row>
              <PageTitleHeader title={'Rewards + Codes'} />
            </Row>
          </Colxx>
          <Col sm='12' md='12' lg='12' xl='10' className='mx-autos'>
            <div
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                display: 'flex',
                marginTop: 10,
                marginBottom: 48,
              }}
            >
              <span style={{ fontSize: '1rem', marginBottom: 15 }}>
                Give a friend ${shop.referral_discount} off their first order
                and you’ll get ${shop.referral_discount} credit to your account
              </span>
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={emailValidationSchema}
              >
                {({
                  values,
                  setFieldValue,
                  setFieldTouched,
                  errors,
                  touched,
                }) => {
                  return (
                    <Form style={{ width: isSM ? '100%' : '75%' }}>
                      <FormGroup>
                        <div
                          style={{
                            display: 'flex',
                            alignItems:
                              errors.email && touched.email
                                ? isSM
                                  ? 'center'
                                  : 'flex-start'
                                : 'center',
                            justifyContent: isSM ? 'center' : 'space-between',
                            marginTop: isSM ? 25 : 0,
                            flexDirection: isSM ? 'column' : 'row',
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              marginRight: isSM ? 0 : 17,
                              marginTop: 0,
                              marginBottom: 0,
                            }}
                          >
                            <CommonFormInput
                              placeholder={
                                windowWidth <= 800
                                  ? 'Enter friend’s email'
                                  : 'Enter your friend’s email address'
                              }
                              style={{ borderRadius: 30, height: 44 }}
                              type='email'
                              name='email'
                              id='email'
                              showError={errors.email && touched.email}
                              error={errors.email}
                            />
                          </div>
                          <ButtonWithLoadingIcon
                            type='submit'
                            color='primary'
                            size='md'
                            height={36}
                            style={{
                              marginBottom:
                                errors.email && touched.email
                                  ? isSM
                                    ? 0
                                    : 15
                                  : 16,
                              marginTop:
                                errors.email && touched.email
                                  ? 0
                                  : isSM
                                  ? 0
                                  : 0,
                              fontFamily: 'Apercu Mono Pro',
                              height: 44,
                            }}
                          >
                            {strings.send}
                          </ButtonWithLoadingIcon>
                        </div>
                      </FormGroup>
                    </Form>
                  );
                }}
              </Formik>
              <div className='d-flex flex-direction-row'>
                <span style={{ fontSize: '1rem', marginRight: 8 }}>or</span>

                <div
                  className='label mb-3'
                  style={{
                    cursor: 'pointer',
                    color: '#2E2B2B',
                    fontFamily: 'Apercu Mono Pro',
                    fontSize: '1rem',
                  }}
                  onClick={() => copyCodeToClipboard(`${refLink}`)}
                >
                  <span className='link-text'>{strings.copy_link}</span>
                </div>
              </div>
            </div>

            {/* rewards */}
            <div style={{ marginBottom: 48 }}>
              <Row style={{ alignItems: 'center' }}>
                <Colxx xxs={'6'} style={{ paddingBottom: 16 }}>
                  <span style={{ fontSize: 24, fontFamily: 'Editorial New' }}>
                    Rewards
                  </span>
                </Colxx>
                <Colxx
                  xxs={'6'}
                  style={{ textAlign: 'right', paddingBottom: 12 }}
                >
                  <div
                    className='d-flex align-items-center justify-content-end'
                    style={{ flexDirection: 'row' }}
                  >
                    <div
                      className={`mr-3 ${isAvailable ? '' : 'text-muted'}`}
                      style={{
                        textDecoration:
                          selectedTab === 1 ? 'underline' : 'none',
                        cursor: 'pointer',
                        fontFamily: selectedTab === 1 ? 'Brown Bold' : 'Brown',
                        opacity: selectedTab === 1 ? null : 0.7,
                      }}
                      onClick={() => {
                        setSelectedTab(1);
                        const userId = localStorage.getItem('userId');
                        getActiveDiscount({ user_id: userId, available: true });
                      }}
                    >
                      Available
                    </div>
                    <div
                      className={!isAvailable ? '' : 'text-muted'}
                      style={{
                        textDecoration:
                          selectedTab === 2 ? 'underline' : 'none',
                        cursor: 'pointer',
                        fontFamily: selectedTab === 2 ? 'Brown Bold' : 'Brown',
                        opacity: selectedTab === 2 ? null : 0.7,
                      }}
                      onClick={() => {
                        setSelectedTab(2);
                        getRedeemedDiscount();
                      }}
                    >
                      Redeemed
                    </div>
                  </div>
                </Colxx>
              </Row>
              <AdvanceTableWrapper
                columns={rewardCols}
                data={discountsList}
                pagination={discountsList.length > 15}
                perPage={pageSizeValue}
                sortable={true}
              >
                <AdvanceTable
                  table
                  headerClassName='text-400 text-nowrap align-middle'
                  rowClassName='align-middle white-space-nowrap'
                  tableProps={{
                    bordered: false,
                    striped: false,
                    loader: loadingDiscounts,
                    className: 'fs--1 mb-0 overflow-x-auto',
                  }}
                />
                {discountsList > 15 ? (
                  <div className='my-5'>
                    <AdvanceTablePagination
                      table
                      rowsPerPageSelection
                      setPageSize={(value) => setPageSizeValue(value)}
                      pageSize={pageSizeValue}
                    />
                  </div>
                ) : null}
              </AdvanceTableWrapper>
            </div>

            {/* referrals       */}
            <div style={{ marginBottom: '5.5rem' }}>
              <Row>
                <Colxx xxs={'12'} style={{ paddingBottom: 16 }}>
                  <span style={{ fontSize: 24, fontFamily: 'Editorial New' }}>
                    Referrals
                  </span>
                </Colxx>
                {/* <Colxx xxs={'6'} style={{ textAlign: 'right' }}>
                <span className='text-muted'>Refer-a-friend</span>
              </Colxx> */}
              </Row>
              <AdvanceTableWrapper
                columns={cols}
                data={referals}
                pagination={referals.length > 15}
                perPage={pageSizeValue}
                sortable={true}
              >
                <AdvanceTable
                  table
                  headerClassName='text-400 text-nowrap align-middle'
                  rowClassName='align-middle white-space-nowrap'
                  tableProps={{
                    bordered: false,
                    striped: false,
                    loader: loadingItems,
                    className: 'fs--1 mb-0 overflow-x-auto',
                  }}
                />
                {referals.length > 15 ? (
                  <div className='my-5'>
                    <AdvanceTablePagination
                      table
                      rowsPerPageSelection
                      setPageSize={(value) => setPageSizeValue(value)}
                      pageSize={pageSizeValue}
                    />
                  </div>
                ) : null}
              </AdvanceTableWrapper>
            </div>
          </Col>
        </Row>
      );
    } else {
      return (
        <Row style={!showNavBar ? { justifyContent: 'center' } : {}}>
          <BecomeMemberComponent />
        </Row>
      );
    }
  }
};
